/**
 * The Smplify Create Patching Policy save chain (§6):
 *
 *   1. POST .../payloads/declaration/{identifier}/validate
 *   2. POST .../mdm/policies                         → { id }
 *   3. For each tag: POST /api/v1/tag/device-tag     (tag the matching devices)
 *   4. POST /api/v1/tag/policy-tag                   (tag the new policy)
 *
 * Without step 3+4 the policy reaches no devices. Failures in tag steps are
 * surfaced but do not roll back the policy — the user can re-scope from the
 * detail page.
 */

import { ApiError } from "@/services/apiClient";
import * as payloadsApi from "@/services/payloadsApi";
import * as policiesApi from "@/services/policiesApi";
import * as tagAssignmentApi from "@/services/tagAssignmentApi";
import * as tagsApi from "@/services/tagsApi";
import { getCurrentUserEmail } from "@/lib/auth";
import type { CreatePolicyRequest } from "@/types/api";
import { buildPayload, platformToPolicyPlatform } from "./buildConfig";
import type { SoftwareUpdateWizardState } from "../types";

export type SaveResult =
  | { kind: "created"; policyId: string; scopeWarning?: string }
  | { kind: "pending_approval"; message: string }
  | { kind: "forbidden"; message: string }
  | { kind: "invalid"; errors: string[] };

function extractErrors(err: unknown): string[] {
  if (err instanceof ApiError) {
    const data = (err as unknown as { data?: { errors?: unknown } }).data;
    if (data && Array.isArray(data.errors)) {
      return (data.errors as unknown[]).map((e) =>
        typeof e === "string" ? e : JSON.stringify(e),
      );
    }
    return [err.message];
  }
  if (err instanceof Error) return [err.message];
  return [String(err)];
}

async function fetchDeviceIdsForTags(tagIds: string[]): Promise<string[]> {
  const sets = await Promise.all(
    tagIds.map(async (id) => {
      try {
        const devices = await tagsApi.getDevicesByTag(id);
        return devices.map((d) => (d as { id?: string; deviceId?: string }).id ?? (d as { deviceId?: string }).deviceId ?? "").filter(Boolean);
      } catch {
        return [];
      }
    }),
  );
  return Array.from(new Set(sets.flat()));
}

export async function runSaveChain(
  state: SoftwareUpdateWizardState,
  opts: { policyId?: string } = {},
): Promise<SaveResult> {
  const payload = buildPayload(state);
  const actor = getCurrentUserEmail() ?? "system";

  // 1) Validate (best-effort; surface clear errors before policy create)
  try {
    const v = await payloadsApi.validatePayload(payload.kind, payload.identifier, payload.config);
    if (v && v.valid === false) {
      return {
        kind: "invalid",
        errors: (v.errors ?? []).map((e) => (typeof e === "string" ? e : JSON.stringify(e))),
      };
    }
    if (v && v.normalizedConfig && typeof v.normalizedConfig === "object") {
      payload.config = v.normalizedConfig as Record<string, unknown>;
    }
  } catch (err) {
    if (err instanceof ApiError) {
      if (err.status === 403) {
        return {
          kind: "forbidden",
          message: "This payload isn't enabled for your tenant. Contact your administrator.",
        };
      }
      if (err.status === 422) {
        return { kind: "invalid", errors: extractErrors(err) };
      }
    }
    // Other errors → continue; backend may still accept the create.
  }

  // 2) Create / update the policy
  const req: CreatePolicyRequest = {
    name: state.name.trim(),
    description: state.description.trim() || undefined,
    platforms: [platformToPolicyPlatform(state.platform)],
    payloads: [payload],
  };

  let policyId = opts.policyId ?? "";
  try {
    if (opts.policyId) {
      await policiesApi.updatePolicy(opts.policyId, req);
    } else {
      const created = await policiesApi.createPolicy(req);
      policyId = created.id;
    }
  } catch (err) {
    if (err instanceof ApiError) {
      if (err.status === 202) {
        return {
          kind: "pending_approval",
          message: "Policy submitted — awaiting approval gate before it activates.",
        };
      }
      if (err.status === 403) {
        return {
          kind: "forbidden",
          message: "Your role can't create software update policies. Contact your administrator.",
        };
      }
      if (err.status === 422) return { kind: "invalid", errors: extractErrors(err) };
    }
    throw err;
  }

  // 3) + 4) Scope the policy via tags
  let scopeWarning: string | undefined;
  if (state.tagIds.length > 0) {
    try {
      const deviceIds = await fetchDeviceIdsForTags(state.tagIds);
      if (deviceIds.length > 0) {
        await tagAssignmentApi.assignDeviceTags({
          device_ids: deviceIds,
          tag_ids: state.tagIds,
          created_by: actor,
          updated_by: actor,
        });
      }
      await tagAssignmentApi.assignPolicyTags({
        policy_ids: [policyId],
        tag_ids: state.tagIds,
        created_by: actor,
        updated_by: actor,
      });
    } catch (err) {
      // Best-effort: don't lose the created policy if tag wiring trips up.
      const dup = tagAssignmentApi.isDuplicatePolicyTagAssignmentError(err);
      if (!dup) {
        scopeWarning =
          err instanceof Error
            ? `Policy saved, but scoping failed: ${err.message}`
            : "Policy saved, but scoping failed.";
      }
    }
  }

  return { kind: "created", policyId, scopeWarning };
}
